import { useState } from "react";
import { X, Plus } from "lucide-react";

/**
 * Tag input for free-form interests. Enter or comma commits a chip.
 * Backspace on empty input removes the last chip.
 */
export default function InterestsInput({ value = [], onChange, max = 15 }) {
  const [draft, setDraft] = useState("");

  const commit = (raw) => {
    const clean = (raw || "").trim().replace(/[,]+$/, "").trim();
    if (!clean) return;
    if (value.includes(clean)) return;
    if (value.length >= max) return;
    onChange([...value, clean]);
    setDraft("");
  };

  const removeAt = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="interests-input">
      <div className="flex flex-wrap items-center gap-2 border border-zinc-300 bg-white px-3 py-2.5 focus-within:border-zinc-950">
        {value.map((t, i) => (
          <span
            key={`${t}-${i}`}
            data-testid={`interest-chip-${i}`}
            className="inline-flex items-center gap-1.5 bg-zinc-950 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white"
          >
            {t}
            <button
              type="button"
              aria-label={`Remove ${t}`}
              data-testid={`interest-remove-${i}`}
              onClick={() => removeAt(i)}
              className="text-zinc-300 hover:text-rose-400"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          data-testid="interest-input-field"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => draft && commit(draft)}
          placeholder={value.length === 0 ? "Type an interest and press Enter…" : "Add another…"}
          className="flex-1 min-w-[140px] bg-transparent px-1 py-1 text-sm outline-none placeholder:text-zinc-400"
          disabled={value.length >= max}
        />
        {draft && (
          <button
            type="button"
            data-testid="interest-add-btn"
            onClick={() => commit(draft)}
            className="inline-flex items-center gap-1 bg-rose-600 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-zinc-950"
          >
            <Plus className="h-3 w-3" /> Add
          </button>
        )}
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
        {value.length}/{max} · Press Enter or comma to add
      </p>
    </div>
  );
}
